/**
 * Harbor Therapy — clinician roster.
 *
 * Single source of truth. Every page that renders therapist info
 * (listing, profile, booking) reads from here.
 *
 * In a real practice, this would come from a CMS or admin database.
 * For the demo, it's a static module — easy to swap.
 *
 * The `availabilitySeed` per therapist makes their booking calendar
 * look distinct: some therapists have lots of openings, some are
 * mostly booked. Generated deterministically in BookingCalendar.
 */

export const SPECIALIZATIONS = [
    "Anxiety",
    "Depression",
    "Trauma & PTSD",
    "Couples & relationships",
    "Burnout & work stress",
    "Identity & LGBTQ+",
    "Grief & loss",
    "Eating concerns",
    "Life transitions",
    "ADHD & executive function",
    "Perinatal & parenting",
    "Cultural & bicultural identity",
];

export const GENDERS = ["Woman", "Man", "Non-binary"];

export const MODALITIES = [
    "Cognitive Behavioral Therapy (CBT)",
    "EMDR",
    "Internal Family Systems (IFS)",
    "Gottman Method",
    "Emotionally Focused Therapy (EFT)",
    "Psychodynamic",
    "Acceptance & Commitment Therapy (ACT)",
    "Dialectical Behavior Therapy (DBT)",
    "Mindfulness-based",
    "Narrative Therapy",
    "Somatic Experiencing",
];

export const THERAPISTS = [
    {
        slug: "maya-reyes",
        name: "Dr. Maya Reyes",
        credential: "LCSW",
        pronouns: "she/her",
        gender: "Woman",
        languages: ["English", "Spanish"],
        portraitColors: ["#4A6B5C", "#C9A77C"],
        photoUrl: "/images/therapists/maya.png",
        specializations: ["Anxiety", "Burnout & work stress", "Cultural & bicultural identity", "Trauma & PTSD"],
        populations: ["Adults", "High-performing professionals"],
        modalities: ["Cognitive Behavioral Therapy (CBT)", "EMDR", "Internal Family Systems (IFS)", "Mindfulness-based"],
        fee: 150,
        coupleFee: 200,
        offersCouples: false,
        yearsInPractice: 10,
        availabilitySeed: 17,
        availabilityNote: "Limited openings — currently waitlisting some weekday slots.",
        education: [
            { year: 2015, label: "MSW, Columbia University" },
            { year: 2016, label: "LCSW, New York" },
            { year: 2018, label: "EMDR Certification, EMDRIA" },
            { year: 2024, label: "LCSW, New Jersey" },
        ],
        bioShort:
            "I work with adults navigating high-stakes work, anxiety, and the long tail of childhood patterns. Direct, warm, and honest.",
        bioLong: [
            "I came to this work the way most people do — by being on the other side of it first. I trained in clinical social work because I wanted a frame for thinking about people that took their context seriously: their families, their histories, their bodies, the systems they live inside of.",
            "In practice, that means I'm not the kind of therapist who stays quiet for fifty minutes and asks what you make of that. I'll ask questions, I'll share what I'm noticing, and I'll be honest if I think you're avoiding something.",
            "Most of my clients come to me when something has broken down or is starting to: a relationship, a job, a sense of self, the way they've always coped. The work is figuring out what's underneath that — and what to do about it.",
        ],
    },
    {
        slug: "david-park",
        name: "David Park",
        credential: "LMHC",
        pronouns: "he/him",
        gender: "Man",
        languages: ["English", "Korean"],
        portraitColors: ["#5B7B8C", "#D4B896"],
        photoUrl: "/images/therapists/david.png",
        specializations: ["Couples & relationships", "Cultural & bicultural identity", "Anxiety", "Life transitions"],
        populations: ["Couples", "Adults", "Asian American clients"],
        modalities: ["Gottman Method", "Emotionally Focused Therapy (EFT)", "Psychodynamic"],
        fee: 175,
        coupleFee: 225,
        offersCouples: true,
        yearsInPractice: 8,
        availabilitySeed: 23,
        availabilityNote: "Accepting new couples and individual clients.",
        education: [
            { year: 2017, label: "MS Mental Health Counseling, NYU" },
            { year: 2018, label: "LMHC, New York" },
            { year: 2020, label: "Gottman Method Level 3, Gottman Institute" },
            { year: 2022, label: "EFT Externship, ICEEFT" },
        ],
        bioShort:
            "Couples therapy that doesn't pretend conflict is solvable in five sessions. I work with the patterns underneath the fights.",
        bioLong: [
            "Most couples I see have tried to fix things on their own and hit a wall. The same fight keeps happening, or one person has stopped showing up, or trust has cracked and nobody knows how to talk about it.",
            "I'm Gottman-trained and pull heavily from Emotionally Focused Therapy. The first few sessions are about understanding the cycle you keep landing in — once we can see it together, the work is figuring out what each person needs to do differently to break it.",
            "I also do individual work, often with clients navigating bicultural identity, family expectations, and the kind of anxiety that doesn't quite fit the standard CBT mold.",
        ],
    },
    {
        slug: "noor-haddad",
        name: "Noor Haddad",
        credential: "LCSW",
        pronouns: "she/her",
        gender: "Woman",
        languages: ["English", "Arabic"],
        portraitColors: ["#8B6F47", "#E8D5B7"],
        photoUrl: "/images/therapists/noor.png",
        specializations: ["Trauma & PTSD", "Anxiety", "Cultural & bicultural identity", "Grief & loss"],
        populations: ["Adults", "Refugees and displaced individuals", "First-generation Americans"],
        modalities: ["EMDR", "Somatic Experiencing", "Internal Family Systems (IFS)"],
        fee: 165,
        offersCouples: false,
        yearsInPractice: 12,
        availabilitySeed: 31,
        availabilityNote: "A few evening slots available; daytime is fuller.",
        education: [
            { year: 2013, label: "MSW, Hunter College" },
            { year: 2015, label: "LCSW, New York" },
            { year: 2017, label: "EMDR Certification" },
            { year: 2021, label: "Somatic Experiencing Practitioner (SEP)" },
        ],
        bioShort:
            "Trauma-focused work for adults whose nervous systems have been carrying too much for too long.",
        bioLong: [
            "Trauma isn't just what happened to you — it's how your body learned to keep you safe afterward. A lot of my clients come in knowing something is off but unable to put it into words. The body usually knows first.",
            "I work primarily with EMDR and somatic approaches because talking about trauma without addressing what your nervous system is doing tends to plateau. We move at the pace your system can actually integrate.",
            "I have particular experience working with first-generation Americans, refugees, and clients carrying intergenerational trauma — the kinds of stories that don't always fit clinical templates.",
        ],
    },
    {
        slug: "alex-rivera",
        name: "Alex Rivera",
        credential: "LMFT",
        pronouns: "they/them",
        gender: "Non-binary",
        languages: ["English", "Spanish"],
        portraitColors: ["#7A5C8E", "#F0C987"],
        photoUrl: "/images/therapists/alex.png",
        specializations: ["Identity & LGBTQ+", "Anxiety", "Depression", "Life transitions"],
        populations: ["LGBTQ+ adults", "Young adults", "Adults exploring identity"],
        modalities: ["Narrative Therapy", "Acceptance & Commitment Therapy (ACT)", "Mindfulness-based"],
        fee: 140,
        offersCouples: true,
        coupleFee: 185,
        yearsInPractice: 6,
        availabilitySeed: 11,
        availabilityNote: "Currently accepting new clients.",
        education: [
            { year: 2018, label: "MA Marriage & Family Therapy, Antioch" },
            { year: 2019, label: "LMFT, New York" },
            { year: 2021, label: "Narrative Therapy training, Dulwich Centre" },
        ],
        bioShort:
            "Identity-affirming work for queer, trans, and questioning adults. Less interested in fixing you, more interested in helping you hear yourself.",
        bioLong: [
            "I do a lot of work with people figuring out who they are — sometimes around gender or sexuality, sometimes around family, vocation, or the version of themselves they were handed and never chose.",
            "Narrative therapy is core to how I work: the stories we tell about ourselves shape what feels possible. A lot of therapy is gently questioning the stories that aren't actually yours, or aren't actually true.",
            "I also see couples and chosen-family configurations of all kinds, including polyamorous and non-traditional partnerships. I'm explicit, never assumes-you-know, and won't pathologize relationship structures that work for you.",
        ],
    },
    {
        slug: "rachel-cohen",
        name: "Dr. Rachel Cohen",
        credential: "PsyD",
        pronouns: "she/her",
        gender: "Woman",
        languages: ["English", "Hebrew"],
        portraitColors: ["#C9756B", "#E8C5A0"],
        photoUrl: "/images/therapists/rachel.png",
        specializations: ["Eating concerns", "Anxiety", "Perinatal & parenting", "Depression"],
        populations: ["Women", "New parents", "Adults with disordered eating"],
        modalities: ["Cognitive Behavioral Therapy (CBT)", "Dialectical Behavior Therapy (DBT)", "Psychodynamic"],
        fee: 200,
        offersCouples: false,
        yearsInPractice: 14,
        availabilitySeed: 41,
        availabilityNote: "Waitlist for new clients — typical wait is 4-6 weeks.",
        education: [
            { year: 2010, label: "PsyD, Ferkauf Graduate School" },
            { year: 2012, label: "Licensed Psychologist, New York" },
            { year: 2014, label: "Postdoctoral fellowship, Mount Sinai Eating Disorders Program" },
            { year: 2019, label: "Postpartum Support International certification" },
        ],
        bioShort:
            "Specialized work in eating concerns, perinatal mental health, and the high-anxiety patterns that often run alongside both.",
        bioLong: [
            "I came to private practice after a decade in hospital and clinic settings, primarily working with adults struggling with eating disorders and the anxious perfectionism that often underlies them.",
            "My approach is structured but not rigid. With eating concerns specifically, I work closely with dietitians and physicians — therapy alone is rarely enough. I'm comfortable with the medical complexity and won't try to do the work alone when it shouldn't be done alone.",
            "I also work with new and expecting parents, particularly those navigating postpartum anxiety, intrusive thoughts, or the disorientation of a self that feels unrecognizable. This work is often shorter-term and very practical.",
        ],
    },
    {
        slug: "marcus-wright",
        name: "Marcus Wright",
        credential: "LCSW",
        pronouns: "he/him",
        gender: "Man",
        languages: ["English"],
        portraitColors: ["#3D5A6C", "#B8956A"],
        photoUrl: "/images/therapists/marcus.png",
        specializations: ["Trauma & PTSD", "Depression", "Identity & LGBTQ+", "Grief & loss"],
        populations: ["Black men", "Adults", "First responders and veterans"],
        modalities: ["EMDR", "Cognitive Behavioral Therapy (CBT)", "Internal Family Systems (IFS)"],
        fee: 160,
        offersCouples: false,
        yearsInPractice: 9,
        availabilitySeed: 19,
        availabilityNote: "Accepting new clients, with priority for evening availability.",
        education: [
            { year: 2016, label: "MSW, Fordham University" },
            { year: 2017, label: "LCSW, New York" },
            { year: 2019, label: "EMDR Certification, EMDRIA" },
            { year: 2022, label: "IFS Level 2, IFS Institute" },
        ],
        bioShort:
            "Therapy for men who were taught not to talk about it. Trauma, grief, and the slow work of being honest with yourself.",
        bioLong: [
            "A lot of my clients are men who've never been to therapy and aren't sure they belong here. I take that seriously — therapy isn't a personality test you pass or fail, and it isn't about saying the right things. It's about telling the truth, often for the first time.",
            "Much of my work is with trauma — including first responders, veterans, and adults carrying childhood experiences that they've spent decades managing around. EMDR and IFS are central to how I work, alongside straight talk.",
            "I also work with Black men specifically — not exclusively, but it's a population I have particular experience with and where I'm comfortable having the conversations that don't always land in less specialized rooms.",
        ],
    },
    {
        slug: "priya-iyer",
        name: "Dr. Priya Iyer",
        credential: "PhD",
        pronouns: "she/her",
        gender: "Woman",
        languages: ["English", "Tamil", "Hindi"],
        portraitColors: ["#6B8E5A", "#D4A574"],
        photoUrl: "/images/therapists/priya.png",
        specializations: ["ADHD & executive function", "Anxiety", "Burnout & work stress", "Life transitions"],
        populations: ["Adults with ADHD", "Graduate students", "Career-focused professionals"],
        modalities: ["Cognitive Behavioral Therapy (CBT)", "Acceptance & Commitment Therapy (ACT)", "Mindfulness-based"],
        fee: 185,
        offersCouples: false,
        yearsInPractice: 11,
        availabilitySeed: 29,
        availabilityNote: "Currently accepting new clients.",
        education: [
            { year: 2014, label: "PhD Clinical Psychology, Rutgers" },
            { year: 2015, label: "Licensed Psychologist, New York" },
            { year: 2018, label: "ADHD certification, ADDA" },
            { year: 2023, label: "Licensed Psychologist, New Jersey" },
        ],
        bioShort:
            "Adult ADHD, anxiety, and the specific kind of burnout that comes from running an executive-function deficit at full speed for too long.",
        bioLong: [
            "Most of my ADHD clients arrive after a late diagnosis or a long suspicion. They've been compensating for years — sometimes successfully, sometimes catastrophically — and are tired of running the same workarounds.",
            "My work is structured and concrete. We map your patterns, identify the systems that are actually breaking down, and build externalized supports that don't depend on your brain cooperating on any given day. CBT and ACT both show up here, alongside skills work that's specific to ADHD rather than generic anxiety treatment.",
            "I work with a lot of graduate students, early-career professionals, and women diagnosed in their 30s and 40s who are reckoning with what their lives might have looked like with earlier support.",
        ],
    },
    {
        slug: "sam-okonkwo",
        name: "Sam Okonkwo",
        credential: "LMHC",
        pronouns: "she/her",
        gender: "Woman",
        languages: ["English", "Igbo"],
        portraitColors: ["#7B5B3F", "#E8B976"],
        photoUrl: "/images/therapists/sam.png",
        specializations: ["Perinatal & parenting", "Depression", "Anxiety", "Cultural & bicultural identity"],
        populations: ["New parents", "Mothers of color", "First-generation Americans"],
        modalities: ["Cognitive Behavioral Therapy (CBT)", "Psychodynamic", "Mindfulness-based"],
        fee: 145,
        offersCouples: false,
        yearsInPractice: 5,
        availabilitySeed: 7,
        availabilityNote: "Wide availability — actively building practice.",
        education: [
            { year: 2019, label: "MS Mental Health Counseling, Columbia" },
            { year: 2020, label: "LMHC, New York" },
            { year: 2022, label: "Postpartum Support International certification" },
        ],
        bioShort:
            "Postpartum and early-parenthood support, particularly for parents of color navigating the cultural gaps in mainstream perinatal care.",
        bioLong: [
            "Becoming a parent is treated as a universal experience but rarely feels like one — especially for parents of color, immigrant parents, and parents whose families are far away or complicated.",
            "I focus on the perinatal period, from preconception anxiety through the first few years. Postpartum depression and anxiety are common and under-diagnosed, particularly in non-white populations where the cultural defaults for what 'a tired new mom' looks like don't always allow for clinical concern.",
            "Outside of perinatal work, I see adults navigating depression, anxiety, and the complicated terrain of being a first-generation American — including the unspoken obligations and inherited grief that often come with that.",
        ],
    },
];

export const findTherapist = (slug) => THERAPISTS.find((t) => t.slug === slug);

export const filterTherapists = ({ specializations = [], genders = [], offersCouples = null }) => {
    return THERAPISTS.filter((t) => {
        if (specializations.length && !specializations.some((s) => t.specializations.includes(s))) {
            return false;
        }
        if (genders.length && !genders.includes(t.gender)) {
            return false;
        }
        if (offersCouples === true && !t.offersCouples) {
            return false;
        }
        return true;
    });
};